/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { DataType, toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import {
  getCommonCodeGroups,
  setCommonCodeGroups,
  getCommonCodeNames,
} from 'apis/admin/CommonCode';
import { CommonCodeGroup } from 'models/admin/CommonCode';
import { CommonYN } from 'models/common/Common';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { CrudCode } from 'models/common/Edit';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { Code, commonYNList } from 'models/common/CommonCode';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { useMessageModalStore } from 'stores/useMessageModalStore';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';

interface SearchParamData {
  editable: boolean;
  callBack?: any;
  searchParamData?: any;
  searchState?: any;
  searchParam?: any;
}

const CodeGroupGrid = (props: SearchParamData) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setflexRef] = useState<any>();
  const [rowData, setRowData] = useState<CommonCodeGroup[]>([]);
  const [wktAreaDivsCdList, setWktAreaDivsCdList] = useState<Code[]>([]);
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const { setMessageModalStateWhenModalOpen } = useMessageModalStore();

  const soprAprvYNList: Code[] = [
    { cmnCd: 'Y', cmnCdNm: '반영' },
    { cmnCd: 'N', cmnCdNm: '미반영' },
  ];

  const layoutDefinition = useMemo(
    () => [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 45,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'detail',
        header: String(t('com.label.상세', '상세')),
        width: 45,
        isReadOnly: true,
        cssClass: 'WijmoPlay',
        cellTemplate: (params) =>
          params.item['crudKey'] === CrudCode.CREATE || params.item['crudKey'] === CrudCode.DELETE
            ? ''
            : `<Button id="btnDetail"></Button>`,
      },
      {
        header: String(t('admin.grid.업무영역', '업무영역')),
        binding: 'wktAreaDivsCd',
        isReadOnly: false,
        align: 'left',
        width: 120,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(wktAreaDivsCdList, 'cmnCd', 'cmnCdNm'),
      },
      {
        header: String(t('admin.grid.그룹코드', '그룹코드')),
        binding: 'cmnGrCd',
        width: 150,
        align: 'left',
        cssClass: 'WijmoPopup',
        cellTemplate: (params) =>
          params.item['crudKey'] === CrudCode.CREATE
            ? params.value
            : `<span>${params.value}</span><Button id="btnGroupCode" />`,
      },
      {
        header: String(t('admin.grid.그룹코드명', '그룹코드명')),
        binding: 'cmnGrCdNm',
        width: 130,
        align: 'left',
      },
      {
        header: String(t('admin.grid.설명', '설명')),
        binding: 'cmnGrCdDesc',
        align: 'left',
      },
      {
        header: String(t('admin.grid.운영반영여부', '운영반영여부')),
        binding: 'soprAprvYn',
        isReadOnly: false,
        width: 120,
        align: 'center',
        dataMap: new DataMap(soprAprvYNList, 'cmnCd', 'cmnCdNm'),
      },
      {
        header: String(t('com.label.사용여부', '사용여부')),
        binding: 'useYn',
        width: 120,
        align: 'center',
        dataMap: new DataMap(commonYNList, 'cmnCd', 'cmnCdNm'),
      },
      {
        header: String(t('admin.grid.메시지코드', '메시지코드')),
        binding: 'msgCtn',
        isReadOnly: false,
        align: 'left',
      },
      {
        header: String(t('admin.grid.정렬순서', '정렬순서')),
        binding: 'sortOrd',
        dataType: DataType.Number,
        width: 80,
        align: 'center',
      },
      {
        header: String(t('admin.grid.비고', '비고')),
        binding: 'rmk',
        align: 'left',
      },
      {
        header: String(t('admin.grid.옵션명1', '옵션명1')),
        binding: 'optValNm1',
      },
      {
        header: String(t('admin.grid.옵션명2', '옵션명2')),
        binding: 'optValNm2',
      },
      {
        header: String(t('admin.grid.옵션명3', '옵션명3')),
        binding: 'optValNm3',
      },
      {
        header: String(t('admin.grid.옵션명4', '옵션명4')),
        binding: 'optValNm4',
      },
      {
        header: String(t('admin.grid.옵션명5', '옵션명5')),
        binding: 'optValNm5',
      },
      {
        header: String(t('admin.grid.옵션명6', '옵션명6')),
        binding: 'optValNm6',
      },
      {
        header: String(t('admin.grid.옵션명7', '옵션명7')),
        binding: 'optValNm7',
      },
      {
        header: String(t('admin.grid.옵션명8', '옵션명8')),
        binding: 'optValNm8',
      },
      {
        header: String(t('admin.grid.옵션명9', '옵션명9')),
        binding: 'optValNm9',
      },
      {
        header: String(t('admin.grid.옵션명10', '옵션명10')),
        binding: 'optValNm10',
      },
      {
        header: String(t('admin.grid.옵션명11', '옵션명11')),
        binding: 'optValNm11',
      },
      {
        header: String(t('admin.grid.옵션명12', '옵션명12')),
        binding: 'optValNm12',
      },
      {
        header: String(t('admin.grid.옵션명13', '옵션명13')),
        binding: 'optValNm13',
      },
      {
        header: String(t('admin.grid.옵션명14', '옵션명14')),
        binding: 'optValNm14',
      },
      {
        header: String(t('admin.grid.옵션명15', '옵션명15')),
        binding: 'optValNm15',
      },
    ],
    [wktAreaDivsCdList]
  );

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'code';
    book.saveAsync(getExcelFileName(t('admin.label.그룹코드목록', '그룹코드목록')));
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      const isReadOnly = item.crudKey !== CrudCode.CREATE;
      // 그룹코드
      if ('cmnGrCd' === binding) {
        toggleClass(cell, 'WijmoPopup', isReadOnly);
        cell.ariaReadOnly = isReadOnly;
      }
      // 그룹코드명
      // if ('cmnGrCdNm' === binding) {
      //   cell.ariaReadOnly = isReadOnly;
      // }
    }
  });

  const onInitialized = (grid) => {
    setflexRef(grid);
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    grid.formatItem.addHandler((s, e) => {
      // 행추가된 건만 체크박스 노출
      if (CellType.RowHeader === e.panel.cellType) {
        const item = e.panel.rows[e.row].dataItem;
        if (item.crudKey !== CrudCode.CREATE) {
          if (e.cell.hasChildNodes()) {
            e.cell.removeChild(e.cell.firstChild);
          }
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.id) {
          // 상세보기
          case 'btnDetail':
            onCellClicked({ ...grid.rows[ht.row].dataItem, crudKey: CrudCode.READ });
            break;
          // 그룹코드
          case 'btnGroupCode':
            setMessageModalStateWhenModalOpen(item.msgCtn, item.cmnGrCdNm, (cmnGrCdNm?: string) => {
              if (cmnGrCdNm && item) {
                setCommonCodeGroups([
                  { ...item, cmnGrCdNm: cmnGrCdNm, crudKey: CrudCode.UPDATE },
                ]).then((result) => result && fnSearchCodeGroup(props.searchParam || {}));
              }
              openMessageBar({
                type: 'confirm',
                content: t('com.label.저장되었습니다.', '저장되었습니다.'),
              });
            });
        }
      }
    });
  };

  const fnSearchCodeGroup = (searchParam) => {
    getCommonCodeGroups(searchParam).then((result: CommonCodeGroup[]) => {
      setRowData(result);
    });
  };

  const btnAddRow = useCallback(() => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      useYn: CommonYN.Y,
      soprAprvYn: CommonYN.Y,
    } as CommonCodeGroup;

    setRowData([newRow, ...rowData]);
  }, [rowData]);

  const btnDelRow = useCallback(() => {
    // 행추가된 건만 삭제 가능
    const selectedRows = gridRef.current.rows.filter(
      (r) => r.isSelected && r.dataItem.crudKey === CrudCode.CREATE
    );
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      delete rowData[item];
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  }, [rowData]);

  const btnSave = () => {
    const valid = rowData
      .map((rowNode, index) => {
        if (
          (rowNode.crudKey == CrudCode.CREATE || rowNode.crudKey == CrudCode.UPDATE) &&
          (rowNode.wktAreaDivsCd == null || rowNode.wktAreaDivsCd == '')
        )
          return `${index + 1} : ${t(
            'admin.msg.업무영역을 입력해주세요.',
            '업무영역을 입력해주세요.'
          )}`;
        if (rowNode.cmnGrCd == null || rowNode.cmnGrCd == '')
          return `${index + 1} : ${t(
            'admin.msg.그룹코드를 입력해주세요.',
            '그룹코드를 입력해주세요.'
          )}`;
        if (rowNode.cmnGrCdNm == null || rowNode.cmnGrCdNm == '')
          return `${index + 1} : ${t(
            'admin.msg.그룹코드명을 입력해주세요.',
            '그룹코드명을 입력해주세요.'
          )}`;
        if (rowNode.sortOrd == null || rowNode.sortOrd == '') rowNode.sortOrd = '0';
      })
      .filter((element) => element !== undefined);
    if (valid?.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return false;
    }

    const saveRows = rowData
      .map((rowNode) => {
        return rowNode.crudKey ? rowNode : null;
      })
      .filter((element) => element !== null) as CommonCodeGroup[];

    if (saveRows?.length == 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return true;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        setCommonCodeGroups(saveRows).then((result) => {
          if (!result) {
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            return false;
          }
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          fnSearchCodeGroup(props.searchParam || {});
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const onCellClicked = (params: any) => {
    props.callBack && params.crudKey != CrudCode.CREATE && props.callBack(params.cmnGrCd);
  };

  useEffect(() => {
    getCommonCodeNames('WKT_AREA_DIVS_CD').then((res) => {
      setWktAreaDivsCdList(res || []);
    });
  }, []);

  useEffect(() => {
    if (props.searchParamData != false) {
      fnSearchCodeGroup(props.searchParam || {});
    }
  }, [props.searchState]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('admin.label.그룹코드목록', '그룹코드목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        {props.editable && (
          <ControlBtnGroup>
            <Button
              css={IconButton.button}
              className="Exceldown"
              onClick={btnExcelExport}
              disableRipple
            >
              {t('com.button.다운로드', '다운로드')}
            </Button>
            <Button css={IconButton.button} className="addRow" disableRipple onClick={btnAddRow}>
              {t('com.button.행추가', '행추가')}
            </Button>
            <Button css={IconButton.button} className="delRow" disableRipple onClick={btnDelRow}>
              {t('com.button.행삭제', '행삭제')}
            </Button>
          </ControlBtnGroup>
        )}
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={230}
        deferResizing={false}
        initialized={onInitialized}
        excludePin={['detail']}
        excludeFilter={['detail']}
        align={'center'}
        beginningEdit={(grid, e) => {
          const binding = grid.columns[e.col].binding;
          const item = grid.rows[e.row].dataItem;
          // readonly 설정
          // 그룹코드, 그룹코드명
          if (['cmnGrCd'].includes(binding)) {
            if (CrudCode.CREATE !== item.crudKey) {
              e.cancel = true;
            }
          }
        }}
        cellEditEnded={(grid, e) => {
          const binding = grid.columns[e.col].binding;
          const item = grid.rows[e.row].dataItem;
          if (binding === 'cmnGrCd') {
            item.msgCtn = 'com.code.' + item.cmnGrCd;
            grid.refresh();
          }
        }}
      />
      <GlobalBtnGroup>
        {props.editable && (
          <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default CodeGroupGrid;
